import { Locale } from "../enums/general"

/**
 * Canonical localized text — both locale keys are always present. This is the
 * shape of data returned from the backend (normalized by
 * `LocalizedTextTransformer`) and the shape stored on entity columns. Use this
 * type for response DTOs, entity fields, and anywhere you read localized
 * content.
 */
export interface LocalizedText {
  // NOTE: Uses string literals because issues with react-hook-form,
  // might be fixed in newer versions
  "nl-NL": string
  "en-GB": string
}

/**
 * In-progress localized text, as produced by form state during editing — a
 * user may have populated only one locale. Use this type for form values,
 * request DTO classes, and editable-field props. Persisted/response data
 * should be typed as {@link LocalizedText} instead.
 */
export interface PartialLocalizedText {
  "nl-NL"?: string
  "en-GB"?: string
}

export const EMPTY_LOCALIZED_TEXT: LocalizedText = { [Locale.Nl]: "", [Locale.En]: "" }

/**
 * Type guard that narrows an unknown or partial value to the complete
 * {@link LocalizedText} form (both locale keys present).
 */
export const isLocalizedText = (value: unknown): value is LocalizedText => {
  return typeof value === "object" && value !== null && "nl-NL" in value && "en-GB" in value
}

/**
 * Returns `true` when every locale in the text is missing or an empty string.
 * Accepts partial values so it can be used directly against form state.
 */
export const isLocalizedTextEmpty = (value: PartialLocalizedText): boolean => {
  return Object.values(value).every((entry) => !entry)
}

/**
 * Normalizes a {@link PartialLocalizedText} (or null/undefined) into the
 * complete {@link LocalizedText} shape by filling missing locales with empty
 * strings. Use at boundaries where request-sourced partial values need to be
 * persisted as strict localized text.
 */
export const toLocalizedText = (value: PartialLocalizedText | null | undefined): LocalizedText => {
  return {
    [Locale.Nl]: value?.[Locale.Nl] ?? "",
    [Locale.En]: value?.[Locale.En] ?? "",
  }
}
